import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, ListGroup, Button, ButtonGroup } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import './styles/pricing.css';

// Enhanced Pricing data keyed by plan type
const pricingData = {
  monthly: [
    {
      name: 'Starter',
      price: '£149.99/mo',
      deliverables: [
        'Responsive site (up to 3 pages)',
        'Brand-aligned UI & basic on-page SEO setup',
        'Initial 30-minute kickoff & strategy call',
        '2 dev hours per month for updates and fixes',
        'Basic analytics setup & monthly performance summary',
        'Email support with 48h SLA'
      ]
    },
    {
      name: 'Growth',
      price: '£299.99/mo',
      deliverables: [
        'Responsive site (up to 5 pages)',
        'UX wireframes & high-fidelity mockups',
        'Comprehensive SEO setup & 1h CMS training',
        '5 dev hours per month for new features and tweaks',
        'Monthly strategy & review call (30 min)',
        'Priority email & Slack support (24h SLA)',
        'Quarterly mini-design refresh'
      ]
    },
    {
      name: 'Scale',
      price: '£499.99/mo',
      deliverables: [
        'Fully custom site (up to 8 pages + blog)',
        'Interactive prototype & full SEO audit',
        '1h CMS & workflow training',
        '10 dev hours per month for advanced enhancements',
        'Weekly check-in calls (15 min)',
        '24/7 chat support & live bug fixes',
        'Monthly analytics report with insights',
        'Priority placement in development queue'
      ]
    }
  ],
  yearly: [
    {
      name: 'Starter',
      price: '£1,490.99/yr',
      deliverables: [
        'Equivalent of 10 months at £149/mo (2 months free)',
        'Complete responsive site (3 pages) + SEO setup',
        'Kickoff strategy call & quarterly review sessions',
        '2 dev hours/month for 12 months',
        'Custom analytics dashboard & monthly reporting',
        'Automatic CMS updates & 1 annual design refresh',
        'Dedicated WhatsApp support channel'
      ]
    },
    {
      name: 'Growth',
      price: '£2,990.99/yr',
      deliverables: [
        'Equivalent of 10 months at £299/mo (2 months free)',
        'Responsive site (5 pages) + advanced wireframes',
        'SEO strategy workshop & detailed site audit',
        'Content calendar planning & 1h CMS training',
        '5 dev hours/month for 12 months',
        'Bi-monthly strategy calls (1h each)',
        'Priority Slack support (12h SLA)',
        'Custom analytics & conversion tracking setup',
        'Rollover of up to 5 unused hours per quarter',
        'Annual UI/UX refresh & performance tuning'
      ]
    },
    {
      name: 'Scale',
      price: '£4,990.99/yr',
      deliverables: [
        'Equivalent of 10 months at £499/mo (2 months free)',
        'Unlimited pages & bespoke feature development',
        'Interactive prototypes & full technical audit',
        '10 dev hours/month for 12 months',
        'Monthly A/B test reports & UX recommendations',
        'Dedicated account manager & priority dev queue',
        'Quarterly on-site or video workshops (1h)',
        'Custom roadmap planning & feature prioritization',
        'Unlimited rollover hours & flexible scheduling'
      ]
    }
  ],
  lifetime: [
    {
      name: 'Essential',
      price: '£499.99',
      deliverables: [
        'Responsive site (up to 3 pages)',
        'Brand-aligned UI design',
        'Basic on-page SEO configuration',
        'CMS setup & 30-minute training',
        '30 days post-launch email support'
      ]
    },
    {
      name: 'Standard',
      price: '£899.99',
      deliverables: [
        'Responsive site (up to 5 pages)',
        'Wireframes & high-fidelity mockups',
        'Comprehensive SEO audit & on-page fixes',
        'CMS setup & 1h training',
        '60 days post-launch email & Slack support',
        'Basic analytics integration with report template'
      ]
    },
    {
      name: 'Premium',
      price: '£1,499.99',
      deliverables: [
        'Responsive site (up to 8 pages + blog)',
        'Interactive prototype & full SEO audit',
        'CMS setup & 2h training session',
        '90 days post-launch support (email, Slack & chat)',
        'Detailed analytics dashboard setup',
        'Optional quarterly check-in (video call)'
      ]
    }
  ]
};

const Pricing = () => {
  const [planType, setPlanType] = useState('monthly');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const sectionTitle = {
    monthly: 'Monthly Plans (3‑month minimum)',
    yearly: 'Yearly Plans (2 months free – billed annually)',
    lifetime: 'One-Time Projects'
  };

  const planExplanation = {
    monthly: 'Monthly plans are ideal for new or growing businesses wanting flexibility. Get a complete site launch plus ongoing monthly support to keep your digital presence adaptive and up-to-date.',
    yearly: 'Yearly plans deliver the best value with two free months, dedicated strategy sessions, priority support, and continuous optimization—ensuring your site evolves seamlessly over the year.',
    lifetime: 'Lifetime one-off projects are perfect for those seeking a straightforward, single investment. Launch your site and get essential support without any recurring fees.'
  };

  return (
    <div className='pricing'>
    <Container className="pricing-page my-5">
      <h2>Pricing Packages.💳</h2>
      <div className="text-center mb-4">
        <ButtonGroup aria-label="Plan toggle">
          {['monthly', 'yearly', 'lifetime'].map(type => (
            <Button
              key={type}
              variant={planType === type ? 'primary' : 'outline-primary'}
              onClick={() => setPlanType(type)}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </Button>
          ))}
        </ButtonGroup>
      </div>

      <div className="plan-explanation text-center mb-4">
        <p>{planExplanation[planType]}</p>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <h3 className="text-center mb-4">{sectionTitle[planType]}</h3>
        <Row>
          {pricingData[planType].map((tier, idx) => (
            <Col xs={12} md={4} key={idx} className="mb-4">
              <Card className="h-100">
                <Card.Body className="d-flex flex-column">
                  <Card.Title>{tier.name}</Card.Title>
                  <h1 className="pricing-price mb-3">{tier.price}</h1>
                  <ul className="pricing-details">
                    {tier.deliverables.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>

                  <Link className='primary-btn mt-auto'>
                      Get Started
                  </Link>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </motion.div>
    </Container>
    </div>
  );
};

export default Pricing;
